import { formatInboxDate } from "./formatEmailDate";
import type { EmailDirectionFilter, ReceivablesEmailThreadRow } from "../types";

export function EmailThreadListRow({
  row,
  direction,
  selected,
  onSelect,
  onInvoiceClick,
  onCustomerClick,
}: {
  row: ReceivablesEmailThreadRow;
  direction: EmailDirectionFilter;
  selected: boolean;
  onSelect: () => void;
  onInvoiceClick: (externalId: string) => void;
  onCustomerClick?: (customerId: number) => void;
}) {
  const unread = row.hasUnread;
  const isSentView = direction === "outgoing" || row.lastMessageIsOutgoing;
  const senderLabel =
    isSentView && row.sendSource === "automated_template"
      ? "Automated"
      : isSentView && row.sentByUsername
        ? row.sentByUsername
        : null;
  const primaryLabel = isSentView
    ? row.linkedCustomerNames[0]?.trim() ||
      (row.linkedInvoiceIds[0] ? `Invoice ${row.linkedInvoiceIds[0]}` : null) ||
      "Recipient"
    : row.fromName?.trim() || row.fromEmail || "Unknown";
  const customerLinkId =
    isSentView && row.linkedCustomerRepId != null ? row.linkedCustomerRepId : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full border-b border-slate-200 px-3 py-3 text-left transition-colors ${
        selected
          ? isSentView
            ? "border-l-4 border-l-teal-600 bg-teal-50/60"
            : "border-l-4 border-l-blue-500 bg-blue-50"
          : unread
            ? "bg-slate-50 hover:bg-slate-100"
            : "hover:bg-slate-50"
      }`}
    >
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {customerLinkId && onCustomerClick ? (
                <span
                  role="link"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCustomerClick(customerLinkId);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onCustomerClick(customerLinkId);
                    }
                  }}
                  className={`cursor-pointer truncate text-sm hover:underline ${
                    unread ? "font-semibold text-blue-700" : "text-blue-600"
                  }`}
                >
                  {primaryLabel}
                </span>
              ) : (
                <span
                  className={`truncate text-sm ${
                    unread ? "font-semibold text-slate-900" : "text-slate-700"
                  }`}
                >
                  {primaryLabel}
                </span>
              )}
              <span
                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  isSentView
                    ? "bg-teal-100 text-teal-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isSentView ? "Sent" : "In"}
              </span>
            </div>
            <span className="shrink-0 text-xs text-slate-500">
              {formatInboxDate(row.lastMessageAt)}
            </span>
          </div>
          <p
            className={`truncate text-sm ${
              unread ? "font-semibold text-slate-900" : "text-slate-800"
            }`}
          >
            {row.subject}
          </p>
          <p className="truncate text-xs text-slate-500">{row.snippet}</p>
          {senderLabel ? (
            <p className="mt-0.5 truncate text-xs text-slate-500">
              Sent by{" "}
              <span className="font-medium text-slate-600">{senderLabel}</span>
            </p>
          ) : null}
          {row.linkedInvoiceIds.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {row.linkedInvoiceIds.slice(0, 3).map((id) => (
                <span
                  key={id}
                  role="link"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onInvoiceClick(id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onInvoiceClick(id);
                    }
                  }}
                  className="cursor-pointer rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-200"
                >
                  {id}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
}
